import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Hash, Image as ImageIcon, Trash2, Copy, Megaphone, MessageSquare, X, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "twitter", label: "X (Twitter)" },
  { id: "threads", label: "Threads" },
  { id: "blog", label: "Blog Article" },
];

const TONES = ["Professional", "Friendly", "Bold", "Luxury", "Playful", "Inspirational"];

const LANGUAGES = [
  "English", "Hindi", "Marathi", "Tamil", "Telugu", "Bengali", "Gujarati",
  "Kannada", "Malayalam", "Punjabi", "Odia", "Assamese", "Urdu",
  "Spanish", "French", "German", "Portuguese", "Italian", "Arabic",
  "Indonesian", "Vietnamese", "Thai", "Japanese", "Korean", "Mandarin",
];

export default function ContentStudio() {
  const [platform, setPlatform] = useState("instagram");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Professional");
  const [language, setLanguage] = useState("English");
  const [hashtags, setHashtags] = useState(true);
  const [withImage, setWithImage] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [items, setItems] = useState([]);
  const [boostItem, setBoostItem] = useState(null);
  const [waTplItem, setWaTplItem] = useState(null);

  const load = async () => {
    const { data } = await api.get("/ai/content");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Enter a topic first");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/content", {
        platform,
        topic,
        tone,
        language,
        include_hashtags: hashtags,
        include_image: withImage,
      });
      setItems([data, ...items]);
      setTopic("");
      toast.success("Content generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/ai/content/${id}`);
    setItems(items.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const copyPost = (item) => {
    const c = item.content || {};
    const txt = `${c.headline || ""}\n\n${c.body || ""}\n\n${(c.hashtags || []).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}\n\n${c.cta || ""}`;
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-8" data-testid="content-studio-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">AI Content Studio</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Write anything. Faster.</h1>
        <p className="text-white/50 mt-2 max-w-2xl">
          Generate posts, captions and full-length articles tuned to your brand voice and audience.
        </p>
      </header>

      <div className="glass rounded-2xl p-6 lg:p-8">
        <div className="grid lg:grid-cols-4 gap-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Platform</label>
            <select
              value={platform}
              data-testid="content-platform-select"
              onChange={(e) => setPlatform(e.target.value)}
              className="mt-2 w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm"
            >
              {PLATFORMS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Tone</label>
            <select
              value={tone}
              data-testid="content-tone-select"
              onChange={(e) => setTone(e.target.value)}
              className="mt-2 w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm"
            >
              {TONES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Language</label>
            <select
              value={language}
              data-testid="content-language-select"
              onChange={(e) => setLanguage(e.target.value)}
              className="mt-2 w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm"
            >
              {LANGUAGES.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-3">
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Options</label>
            <div className="flex gap-4 items-center">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="content-hashtags-toggle"
                  checked={hashtags}
                  onChange={(e) => setHashtags(e.target.checked)}
                  className="accent-orange-500"
                />
                <Hash className="h-3.5 w-3.5" /> Hashtags
              </label>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="content-image-toggle"
                  checked={withImage}
                  onChange={(e) => setWithImage(e.target.checked)}
                  className="accent-orange-500"
                />
                <ImageIcon className="h-3.5 w-3.5" /> Image
              </label>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <label className="text-xs uppercase tracking-[0.2em] text-white/40">What's the post about?</label>
          <textarea
            rows={3}
            value={topic}
            data-testid="content-topic-input"
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Launching our new sustainable summer collection with 30% off for early customers"
            className="mt-2 w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm"
          />
        </div>

        <button
          onClick={generate}
          data-testid="content-generate-button"
          disabled={generating}
          className="mt-5 inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange disabled:opacity-50"
        >
          <Sparkles className="h-4 w-4" />
          {generating ? "Generating…" : "Generate content"}
        </button>
      </div>

      <section>
        <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">History</div>
        {items.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">
            No content yet. Generate your first piece above.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((it) => {
              const c = it.content || {};
              return (
                <div key={it.id} className="glass rounded-2xl p-6" data-testid={`content-item-${it.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded-full glass">
                      {it.platform}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setBoostItem(it)}
                        data-testid={`boost-${it.id}`}
                        title="Boost as Meta Ad"
                        className="p-2 rounded-lg hover:bg-indigo-500/10"
                      >
                        <Megaphone className="h-4 w-4 text-indigo-300" />
                      </button>
                      <button
                        onClick={() => setWaTplItem(it)}
                        data-testid={`wa-template-${it.id}`}
                        title="Submit as WhatsApp template"
                        className="p-2 rounded-lg hover:bg-green-500/10"
                      >
                        <MessageSquare className="h-4 w-4 text-green-400" />
                      </button>
                      <button
                        onClick={() => copyPost(it)}
                        data-testid={`copy-${it.id}`}
                        className="p-2 rounded-lg hover:bg-white/5"
                      >
                        <Copy className="h-4 w-4 text-white/60" />
                      </button>
                      <button
                        onClick={() => remove(it.id)}
                        data-testid={`delete-${it.id}`}
                        className="p-2 rounded-lg hover:bg-white/5"
                      >
                        <Trash2 className="h-4 w-4 text-white/60" />
                      </button>
                    </div>
                  </div>
                  {it.image_b64 && (
                    <img
                      src={`data:image/png;base64,${it.image_b64}`}
                      alt="Generated"
                      className="rounded-lg w-full mb-4 border border-white/10"
                    />
                  )}
                  {c.headline && <h3 className="font-heading font-bold text-lg mb-2">{c.headline}</h3>}
                  <p className="text-sm text-white/70 whitespace-pre-wrap leading-relaxed">{c.body}</p>
                  {c.hashtags && c.hashtags.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {c.hashtags.map((h, i) => (
                        <span key={i} className="text-[11px] text-orange-400 font-mono">
                          #{String(h).replace(/^#/, "")}
                        </span>
                      ))}
                    </div>
                  )}
                  {c.cta && (
                    <div className="mt-3 text-sm font-semibold brand-gradient-text">{c.cta}</div>
                  )}
                  {c.best_time_to_post && (
                    <div className="mt-3 text-[11px] text-white/40 font-mono uppercase tracking-[0.2em]">
                      Best time: {c.best_time_to_post}
                    </div>
                  )}
                  {c.raw && (
                    <pre className="mt-3 text-xs text-white/60 whitespace-pre-wrap font-mono">{c.raw}</pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {boostItem && (
        <BoostAdModal item={boostItem} onClose={() => setBoostItem(null)} />
      )}
      {waTplItem && (
        <WaTemplateModal item={waTplItem} onClose={() => setWaTplItem(null)} />
      )}
    </div>
  );
}

function BoostAdModal({ item, onClose }) {
  const c = item.content || {};
  const [platform, setPlatform] = useState("meta");
  const [budget, setBudget] = useState(500);
  const [landing, setLanding] = useState("");
  const [objective, setObjective] = useState("LINK_CLICKS");
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/ads/status").then((r) => setStatus(r.data));
  }, []);

  const push = async () => {
    if (!landing.trim()) { toast.error("Landing URL required"); return; }
    setPushing(true);
    try {
      const { data } = await api.post(`/ads/from-content/${item.id}`, {
        platform, daily_budget_inr: budget, landing_url: landing.trim(), objective,
      });
      setResult(data);
      toast.success(`Ad pushed · ${data.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    } finally { setPushing(false); }
  };

  const readyPlatforms = status ? ["meta", "google", "linkedin"].filter((p) => status[p]?.ready) : [];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70" onClick={onClose}>
      <div className="glass-strong border border-indigo-500/30 rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="boost-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-black flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-indigo-300" /> Boost as Ad
          </h3>
          <button onClick={onClose}><X className="h-5 w-5 text-white/40" /></button>
        </div>

        <div className="glass rounded-lg p-3 text-xs space-y-1 mb-4">
          <div className="text-white/40 uppercase tracking-[0.2em] text-[10px]">Post preview</div>
          {c.headline && <div className="font-semibold">{c.headline}</div>}
          {c.body && <div className="text-white/60 line-clamp-3 whitespace-pre-wrap">{c.body}</div>}
        </div>

        {status && readyPlatforms.length === 0 && (
          <div className="glass rounded-lg p-3 text-xs text-amber-300 border border-amber-500/30 mb-4">
            No ad platforms are ready. <a href="/dashboard/ads" className="underline">Link an ad account at /dashboard/ads</a>.
          </div>
        )}

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Platform</span>
            <select value={platform} onChange={(e) => setPlatform(e.target.value)}
              data-testid="boost-platform"
              className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono">
              {(readyPlatforms.length ? readyPlatforms : ["meta"]).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Daily budget (₹)</span>
            <input type="number" value={budget} min={100} max={1000000}
              onChange={(e) => setBudget(parseInt(e.target.value || 0, 10))}
              data-testid="boost-budget"
              className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          </label>
          <label className="block">
            <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Landing URL</span>
            <input value={landing} onChange={(e) => setLanding(e.target.value)}
              placeholder="https://yoursite.com/offer"
              data-testid="boost-landing"
              className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          </label>
          <label className="block">
            <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Objective</span>
            <select value={objective} onChange={(e) => setObjective(e.target.value)}
              data-testid="boost-objective"
              className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="LINK_CLICKS">Traffic / Link clicks</option>
              <option value="LEAD_GEN">Lead generation</option>
              <option value="REACH">Reach</option>
              <option value="BRAND_AWARENESS">Brand awareness</option>
              <option value="CONVERSIONS">Conversions</option>
            </select>
          </label>
        </div>

        {result ? (
          <div className="mt-5 glass rounded-lg p-3 text-xs border border-emerald-500/30 bg-emerald-500/[0.04]" data-testid="boost-result">
            <div className="text-emerald-300 font-semibold mb-1">Pushed as {result.status}</div>
            {result.campaign_id && <div className="text-white/60">Campaign ID: <code className="text-indigo-300">{result.campaign_id}</code></div>}
            {result.review_url && (
              <a href={result.review_url} target="_blank" rel="noreferrer" className="text-indigo-300 underline mt-1 inline-flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Review in ad manager
              </a>
            )}
          </div>
        ) : (
          <button onClick={push} disabled={pushing || readyPlatforms.length === 0}
            data-testid="boost-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
            Push as draft ad
          </button>
        )}
      </div>
    </div>
  );
}

// Maps human language label → WABA language code (mirrors backend mapping)
const WABA_LANG_FROM_HUMAN = {
  English: "en_US", Hindi: "hi", Marathi: "mr", Tamil: "ta", Telugu: "te",
  Bengali: "bn", Gujarati: "gu", Kannada: "kn", Malayalam: "ml", Punjabi: "pa",
  Odia: "or_IN", Assamese: "as", Urdu: "ur", Spanish: "es", French: "fr",
  German: "de", Portuguese: "pt_BR", Italian: "it", Arabic: "ar",
  Indonesian: "id", Vietnamese: "vi", Thai: "th", Japanese: "ja",
  Korean: "ko", Mandarin: "zh_CN",
};

function WaTemplateModal({ item, onClose }) {
  const c = item.content || {};
  const defaultName = (c.headline || item.topic || "tpl")
    .toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "").slice(0, 60);

  const [name, setName] = useState(defaultName);
  const [category, setCategory] = useState("MARKETING");
  const [lang, setLang] = useState(WABA_LANG_FROM_HUMAN[item.language] || "en_US");
  const [buttonText, setButtonText] = useState((c.cta || "").slice(0, 25));
  const [buttonUrl, setButtonUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/whatsapp/templates/from-content/${item.id}`, {
        template_name: name,
        category,
        language: lang,
        button_text: buttonText.trim() || null,
        button_url: buttonUrl.trim() || null,
      });
      setResult(data);
      toast.success(`Template submitted · ${data.wa_status}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submit failed";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70" onClick={onClose}>
      <div
        className="glass-strong border border-green-500/30 rounded-2xl p-6 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="wa-template-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-black flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-green-400" /> Submit as WhatsApp template
          </h3>
          <button onClick={onClose}><X className="h-5 w-5 text-white/40" /></button>
        </div>

        <div className="glass rounded-lg p-3 text-xs space-y-1 mb-4">
          <div className="text-white/40 uppercase tracking-[0.2em] text-[10px]">
            Source content · {item.language || "English"}
          </div>
          {c.headline && <div className="font-semibold">{c.headline}</div>}
          {c.body && <div className="text-white/60 line-clamp-4 whitespace-pre-wrap">{c.body}</div>}
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Template name</span>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              data-testid="wa-tpl-name"
              className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
            />
            <span className="text-[10px] text-white/40 mt-1 block">
              Lowercase + underscores + numbers only. Meta enforces.
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Category</span>
              <select
                value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="wa-tpl-category"
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utility</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Language code</span>
              <input
                value={lang} onChange={(e) => setLang(e.target.value)}
                data-testid="wa-tpl-lang"
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Button label (optional)</span>
              <input
                value={buttonText} onChange={(e) => setButtonText(e.target.value)}
                placeholder="Shop now"
                maxLength={25}
                data-testid="wa-tpl-button-text"
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Button URL</span>
              <input
                value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)}
                placeholder="https://…"
                data-testid="wa-tpl-button-url"
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>
        </div>

        {result ? (
          <div className="mt-5 glass rounded-lg p-3 text-xs border border-emerald-500/30 bg-emerald-500/[0.04]" data-testid="wa-tpl-result">
            <div className="text-emerald-300 font-semibold mb-1 inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Submitted to Meta · {result.wa_status}
            </div>
            <div className="text-white/60">Template ID: <code className="text-emerald-300">{result.wa_template_id}</code></div>
            <div className="text-white/40 mt-1">Meta typically reviews within 24 hours. Track at /dashboard/whatsapp → Templates.</div>
          </div>
        ) : (
          <button
            onClick={submit} disabled={busy}
            data-testid="wa-tpl-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
            Submit for Meta approval
          </button>
        )}
      </div>
    </div>
  );
}

